import { CiiService } from "../../services/cii/service";
import mapCiiRatingsDataFromJson from "../../domain/cii/ratings/mapper";
import { ciiContributionDataFromJson } from "../../domain/cii/contribution/mapper";
import mapCiiCurrentDataFromJson from "../../domain/cii/current/mapper";
import mapCiiVoyageDataFromJson from "../../domain/cii/voyage/mapper";
import { ciiCalculationModule } from "./calculation";
import { foulingModule } from "./fouling";
import { _generateCiiHistoryChartOptions, _generateCiiSeriesData } from "./utils/cii_history_chart_options";
import { _generateCiiAverageChartOptions, _generateCiiAverageSeriesData } from "./utils/cii_average_chart_options";
import mapCiiCalculationResultDataFromJson from "../../domain/cii/calculation/result/mapper";

const ciiService = new CiiService(
  localStorage.getItem("basicUrl"),
  mapCiiRatingsDataFromJson,
  ciiContributionDataFromJson,
  mapCiiCurrentDataFromJson,
  mapCiiVoyageDataFromJson,
  mapCiiCalculationResultDataFromJson
);

const getDefaultState = () => {
  return {
    ciiRatings: null,
    ciiRatingsLoading: false,
    ciiRatingsError: null,
    ciiContributionValues: null,
    ciiContributionValuesLoading: true,
    ciiContributionValuesError: null,
    ciiHistory: null,
    ciiHistoryLoading: true,
    ciiHistoryError: null,
    voyageDataForCiiHistory: null,
    voyageDataForCiiHistoryLoading: false,
    voyageDataForCiiHistoryError: null,
  };
};

const state = getDefaultState();

const getters = {
  ciiHistoryChartOptions: (state, getters) => {
    return _generateCiiHistoryChartOptions(state.ciiRatings, getters.ciiSeries, getters.ciiNoEffectSeries, getters.ciiNoFoulingSeries, getters.ciiNoWeatherSeries);
  },
  ciiSeries: (state) => {
    return _generateCiiSeriesData(state.ciiHistory, "cii");
  },
  ciiNoEffectSeries: (state) => {
    return _generateCiiSeriesData(state.ciiHistory, "noEffectCii");
  },
  ciiNoFoulingSeries: (state) => {
    return _generateCiiSeriesData(state.ciiHistory, "noFouling");
  },
  ciiNoWeatherSeries: (state) => {
    return _generateCiiSeriesData(state.ciiHistory, "noWindCii");
  },
  ciiAverageChartOptions: (state) => {
    return _generateCiiAverageChartOptions(state.voyageDataForCiiHistory, state.ciiHistory, state.ciiRatings);
  },
  ciiAverageSeriesData: (state) => {
    return _generateCiiAverageSeriesData(state.ciiHistory, state.voyageDataForCiiHistory);
  },
  tableData: (state) => {
    if (state.ciiContributionValues) {
      return state.ciiContributionValues.toList();
    }
    return [];
  },
};

const actions = {
  async fetchCiiRatings({ commit }, vesselName) {
    commit("SET_FETCH_CII_RATINGS_LOADING");
    try {
      const currentYear = new Date().getFullYear();
      const ciiRatings = await ciiService.getCiiRatings(localStorage.getItem("quad_token"), vesselName, currentYear);
      commit("SET_FETCH_CII_RATINGS_SUCCESS", ciiRatings);
    } catch (error) {
      commit("SET_FETCH_CII_RATINGS_FAILED");
    }
  },

  async fetchCiiContributionValues({ commit }, payload) {
    commit("SET_FETCH_CII_CONTRIBUTION_VALUES_LOADING");
    try {
      const ciiContributionValues = await ciiService.getCiiContributionValues(
        localStorage.getItem("quad_token"), payload.vessel, payload.date
      );
      commit("SET_FETCH_CII_CONTRIBUTION_VALUES_SUCCESS", ciiContributionValues);
    } catch (error) {
      commit("SET_FETCH_CII_CONTRIBUTION_VALUES_FAILED");
    }
  },

  async fetchCiiHistory({ commit }, vesselName) {
    commit("SET_FETCH_CII_HISTORY_LOADING");
    try {
      let currDate = new Date();

      if (process.env.VUE_APP_COMPANY_ID === "4") {
        currDate = new Date(2024, 11, 31);
      }

      const startDate = (currDate.getMonth() > 3)
        ? new Date(currDate.getFullYear(), 0, 1).toISOString().substring(0, 10)
        : new Date(currDate.getFullYear() - 1, 9, 1).toISOString().substring(0, 10);

      const ciiHistory = await ciiService.getCiiHistory(
        localStorage.getItem("quad_token"),
        vesselName,
        startDate,
        currDate.toISOString().substring(0, 10)
      );
      commit("SET_FETCH_CII_HISTORY_SUCCESS", ciiHistory);
    } catch (error) {
      commit("SET_FETCH_CII_HISTORY_FAILED");
    }
  },
  async fetchVoyageDataForCiiHistory({ commit }, vesselName) {
    commit("SET_FETCH_VOYAGE_DATA_FOR_CII_HISTORY_LOADING");
    try {
      let currDate = new Date();

      if (process.env.VUE_APP_COMPANY_ID === "4") {
        currDate = new Date(2024, 11, 31);
      }

      let startDate = (currDate.getMonth() > 3)
        ? new Date(currDate.getFullYear(), 0, 1).toISOString().substring(0, 10)
        : new Date(currDate.getFullYear() - 1, 9, 1).toISOString().substring(0, 10);

      if (currDate.getMonth() > 3) startDate = new Date(currDate.getFullYear(), 0, 1).toISOString().substring(0, 10);
      else startDate = new Date(currDate.getFullYear() - 1, 9, 1).toISOString().substring(0, 10);
      const voyageDataForCiiHistory = await ciiService.getVoyageDataForCiiHistory(localStorage.getItem("quad_token"), vesselName, startDate, currDate.toISOString().substring(0, 10));
      commit("SET_FETCH_VOYAGE_DATA_FOR_CII_HISTORY_SUCCESS", voyageDataForCiiHistory);
    } catch (error) {
      commit("SET_FETCH_VOYAGE_DATA_FOR_CII_HISTORY_FAILED");
    }
  },

  async fetchInitialData({ dispatch }, vesselName) {
    await Promise.all([
      dispatch("fetchCiiRatings", vesselName),
      dispatch("fetchCiiContributionValues", {
        vessel: vesselName,
        date: new Date().toISOString().substring(0, 10),
      }),
      dispatch("fetchCiiHistory", vesselName),
      dispatch("fetchVoyageDataForCiiHistory", vesselName),
      dispatch("fouling/fetchFoulingData", vesselName),
    ]);
  }
};

const mutations = {
  SET_FETCH_CII_RATINGS_SUCCESS(state, ciiRatings) {
    state.ciiRatings = ciiRatings;
    state.ciiRatingsLoading = false;
    state.ciiRatingsError = null;
  },
  SET_FETCH_CII_CONTRIBUTION_VALUES_SUCCESS(state, ciiContributionValues) {
    state.ciiContributionValues = ciiContributionValues;
    state.ciiContributionValuesLoading = false;
    state.ciiContributionValuesError = null;
  },
  SET_FETCH_CII_HISTORY_SUCCESS(state, ciiHistory) {
    state.ciiHistory = ciiHistory;
    state.ciiHistoryLoading = false;
    state.ciiHistoryError = null;
  },
  SET_FETCH_CII_RATINGS_LOADING(state) {
    state.ciiRatingsLoading = true;
    state.ciiRatingsError = null;
  },
  SET_FETCH_CII_CONTRIBUTION_VALUES_LOADING(state) {
    state.ciiContributionValuesLoading = true;
    state.ciiContributionValuesError = null;
  },
  SET_FETCH_CII_HISTORY_LOADING(state) {
    state.ciiHistoryLoading = true;
    state.ciiHistoryError = null;
  },
  SET_FETCH_CII_RATINGS_FAILED(state) {
    state.ciiRatingsLoading = false;
    state.ciiRatingsError = true;
  },
  SET_FETCH_CII_CONTRIBUTION_VALUES_FAILED(state) {
    state.ciiContributionValuesLoading = false;
    state.ciiContributionValuesError = true;
  },
  SET_FETCH_CII_HISTORY_FAILED(state) {
    state.ciiHistoryLoading = false;
    state.ciiHistoryError = true;
  },
  SET_FETCH_VOYAGE_DATA_FOR_CII_HISTORY_SUCCESS(state, voyageDataForCiiHistory) {
    state.voyageDataForCiiHistory = voyageDataForCiiHistory;
    state.voyageDataForCiiHistoryLoading = false;
    state.voyageDataForCiiHistoryError = null;
  },
  SET_FETCH_VOYAGE_DATA_FOR_CII_HISTORY_LOADING(state) {
    state.voyageDataForCiiHistoryLoading = true;
    state.voyageDataForCiiHistoryError = null;
  },
  SET_FETCH_VOYAGE_DATA_FOR_CII_HISTORY_FAILED(state) {
    state.voyageDataForCiiHistoryLoading = false;
    state.voyageDataForCiiHistoryError = true;
  },
  UPDATE_CII_CONTRIBUTION_VALUES(state, calculationResult) {
    state.ciiContributionValues.cii.input = calculationResult.cii;
    state.ciiContributionValues.distance.input = calculationResult.distance;
    state.ciiContributionValues.averageLadenMeFoc.input = calculationResult.averageLadenMeFoc;
    state.ciiContributionValues.averageBallastMeFoc.input = calculationResult.averageBallastMeFoc;
  },
  RESET_CII_CONTRIBUTION_VALUES(state) {
    Object.keys(state.ciiContributionValues).forEach((key) => {
      state.ciiContributionValues[key].input = null;
    });
  }
};

export const ciiModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    calculation: ciiCalculationModule,
    fouling: foulingModule
  }
};
